import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Trung Gian 247';
const SITE_URL  = 'https://trunggian247.com';
const DEFAULT_IMG = `${SITE_URL}/og-image.jpg`;

/**
 * SEOHead — đặt vào mỗi trang để quản lý title, meta, canonical, OG, Twitter Card, JSON-LD.
 *
 * Props:
 *   title       — tiêu đề trang (không cần thêm tên site)
 *   description — mô tả trang (~150-160 ký tự)
 *   canonical   — đường dẫn (vd: "/faq"), sẽ tự gắn domain
 *   ogImage     — URL ảnh OG (tùy chọn, mặc định DEFAULT_IMG)
 *   ogType      — "website" | "article" (mặc định "website")
 *   noIndex     — true nếu không muốn Google index (trang đăng nhập, dashboard,...)
 *   jsonLd      — schema.org JSON-LD object (tùy chọn)
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noIndex = false,
  jsonLd,
  children,
}) => {
  const fullTitle      = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} – Ký quỹ trực tuyến an toàn tại Việt Nam`;
  const canonicalUrl   = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const imageUrl       = ogImage || DEFAULT_IMG;
  const metaDesc       = description || 'Trunggian247 – Nền tảng ký quỹ trực tuyến tại Việt Nam. Bảo vệ người mua và người bán, hỗ trợ thanh toán ngân hàng & USDT, phí khoảng 3%.';

  return (
    <Helmet>
      {/* ── Basic ── */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      }
      <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'} />

      {/* ── Open Graph ── */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:type"        content={ogType} />
      <meta property="og:image"       content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name"   content={SITE_NAME} />
      <meta property="og:locale"      content="vi_VN" />

      {/* ── Twitter Card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image"       content={imageUrl} />

      {/* ── JSON-LD Structured Data ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}

      {children}
    </Helmet>
  );
};

export default SEOHead;
